import { useState, useEffect, useRef } from 'react';
import { fetchDocFileObjectUrl } from '../utils/api';
import Icon from './Icon';
import { RotatedImg, RotatedFrame } from './RotatedScan';
import { pageRotation } from '../utils/pageRotation';

// The scanned-document pane of the side-by-side review layout. Shared
// verbatim by DocumentViewPage edit mode and the UploadDocumentPage review
// stage so both correction screens stay in lockstep.
//
// • files entries are either { file_path } (a stored upload — fetched as a
//   blob via fetchDocFileObjectUrl, since the authed /api/files route needs
//   the JWT + tenant headers an <img src> can't carry) or { url, isPdf } (a
//   ready-made object URL owned by the caller — used by the upload review
//   stage, where the file isn't linked to a saved document yet so /api/files
//   would 404 it; the pane never revokes caller-owned URLs).
// • `prefetched` ({ filePath, url, pending }) is a page the caller already
//   fetched — the review queue's next draft (utils/draftPrefetch.js). When
//   the current page is that file the pane shows it without a fetch, and,
//   caller-owned, never revokes it. `pending`: still downloading — the pane
//   shows its loader and waits rather than fetching it a second time; the
//   caller then passes the url, or null and the pane fetches it itself.
// • Images get zoom buttons + drag-to-pan + double-click zoom. PDFs render in
//   an iframe and rely on the browser's built-in PDF zoom controls.
// • "פתח בחלון נפרד" opens the current page in a new tab (dual-monitor users)
//   and collapses the pane so the fields get full width. The object URL handed
//   to the new tab is intentionally NOT revoked — the new tab owns it now;
//   it dies with the opener document.
// • Each page shows turned by its saved `rotation`; `onRotate` (admins on a
//   saved document — the upload review stage passes none) adds the turn
//   button and gets the current file.
// Pane width as % of the split container. Operator-adjustable via the drag
// handle on the pane's fields-side edge; persisted so the preferred split
// survives navigation between drafts.
const PANE_WIDTH_KEY = 'ri-doc-pane-width';
const PANE_MIN = 28;
const PANE_MAX = 72;
// 40, not 48: the 13-column items table beside it needs the room (at 48 the
// totals and credit columns sat off-screen on a 1280px laptop).
const PANE_DEFAULT = 40;
const clampWidth = (n) => Math.min(PANE_MAX, Math.max(PANE_MIN, n));

function initialPaneWidth(key, fallback) {
  const saved = parseFloat(localStorage.getItem(key));
  return Number.isFinite(saved) ? clampWidth(saved) : fallback;
}

// `widthKey` / `defaultWidth`: a host with its own split remembers its own
// width. `placement`: which physical side of the split the pane is on — 'left'
// moves the handle to its other edge and measures from the split's left.
export default function ReviewDocPane({
  files, onRotate, prefetched = null,
  widthKey = PANE_WIDTH_KEY, defaultWidth = PANE_DEFAULT, placement = 'right',
}) {
  const [pageIdx,   setPageIdx]   = useState(0);
  const [url,       setUrl]       = useState('');
  const [error,     setError]     = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan,  setPan]  = useState({ x: 0, y: 0 });
  const [widthPct, setWidthPct] = useState(() => initialPaneWidth(widthKey, defaultWidth));
  // Beside the resize ref because the class needs a render: over a PDF the
  // iframe would swallow the drag's mousemove and the handle would stick.
  const [resizing, setResizing] = useState(false);
  const dragRef = useRef(null); // { startX, startY, baseX, baseY } while panning the image
  const paneRef = useRef(null);
  const resizeRef = useRef(false); // true while the width handle is being dragged

  const list = Array.isArray(files) ? files.filter(f => f && (f.file_path || f.url)) : [];
  const idx  = Math.min(Math.max(0, pageIdx), Math.max(0, list.length - 1));
  const cur  = list[idx];
  const fp   = cur?.file_path;
  const warmUrl = fp && prefetched?.url && prefetched.filePath === fp ? prefetched.url : '';
  const awaitingPrefetch = Boolean(fp && prefetched?.pending && prefetched.filePath === fp);
  const directUrl = cur?.url || warmUrl;
  const isPdf = cur?.url ? !!cur?.isPdf : /\.pdf(\?|$)/i.test(fp || '');
  const multi = list.length > 1;
  const rotation = pageRotation(cur);

  // Reset zoom/pan when flipping pages or documents.
  useEffect(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, [fp, directUrl]);

  // Resolve the current page's URL. Caller-owned direct URLs are used as-is;
  // stored files are blob-fetched (JWT via axios), and stale object URLs are
  // revoked so paging through a multi-page doc doesn't accumulate blobs.
  useEffect(() => {
    if (collapsed || (!fp && !directUrl)) { setUrl(''); return; }
    if (awaitingPrefetch) {
      setUrl('');
      setError(false);
      setLoading(true);
      return; // the caller's download lands as `prefetched.url`
    }
    if (directUrl) {
      setUrl(directUrl);
      setError(false);
      setLoading(false);
      return; // caller owns the URL — nothing to revoke here
    }
    let cancelled = false;
    let created = '';
    setError(false);
    setLoading(true);
    fetchDocFileObjectUrl(fp).then(u => {
      if (cancelled) {
        if (u && u.startsWith('blob:')) URL.revokeObjectURL(u);
        return;
      }
      created = u && u.startsWith('blob:') ? u : '';
      setUrl(u || '');
      setLoading(false);
    }).catch(err => {
      if (!cancelled) {
        console.error('[review-doc-pane]', err);
        setUrl('');
        setError(true);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [fp, directUrl, collapsed, awaitingPrefetch]);

  // Drag-to-pan (images) + width-handle resize. Listeners attach to window so
  // neither drag sticks when the cursor leaves the pane.
  useEffect(() => {
    const onMove = (e) => {
      if (resizeRef.current) {
        const container = paneRef.current?.parentElement;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        // On the RIGHT of the RTL split the width grows as the pointer moves
        // right-to-left from the container's right edge; on the left, the
        // mirror of that.
        const px = placement === 'left' ? e.clientX - rect.left : rect.right - e.clientX;
        const pct = clampWidth((px / rect.width) * 100);
        setWidthPct(pct);
        return;
      }
      const d = dragRef.current;
      if (!d) return;
      setPan({ x: d.baseX + (e.clientX - d.startX), y: d.baseY + (e.clientY - d.startY) });
    };
    const onUp = () => {
      dragRef.current = null;
      if (resizeRef.current) {
        resizeRef.current = false;
        setResizing(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        setWidthPct(w => { localStorage.setItem(widthKey, String(Math.round(w))); return w; });
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [widthKey, placement]);

  if (list.length === 0) return null;

  const openExternal = async () => {
    try {
      // Direct URLs open as-is; stored files get a fresh object URL for the
      // new tab — see header comment for lifecycle. A prefetched page too:
      // its owner revokes it when the review moves on.
      const u = cur?.url || await fetchDocFileObjectUrl(fp);
      if (u) window.open(u, '_blank', 'noopener');
      setCollapsed(true);
    } catch (err) {
      console.error('[review-doc-pane] open external failed', err);
    }
  };

  // Collapsed → a thin vertical rail on the split's edge, so the items table
  // gets almost the full width but the document is one click (or the keyboard)
  // away. Sits inline in the flex row (not floating) so nothing overlaps.
  if (collapsed) {
    return (
      <div className="review-doc-pane collapsed" title="הצג את המסמך הסרוק">
        <button
          type="button"
          className="review-doc-rail-btn"
          onClick={() => setCollapsed(false)}
        >
          <Icon name="documents" size={16} className="review-doc-rail-icon" />
          <span className="review-doc-rail-label">הצג מסמך</span>
        </button>
      </div>
    );
  }

  const zoomBtn = { padding: '2px 8px', border: '1px solid #dde3ec', borderRadius: 5, background: '#fff', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700, color: '#0f3460' };

  return (
    <div className={`review-doc-pane${placement === 'left' ? ' is-left' : ''}${resizing ? ' is-resizing' : ''}`}
         dir="rtl" ref={paneRef} style={{ flex: `0 0 ${widthPct}%` }}>
      {/* Width handle on the fields-side (left) edge — drag to resize the
          split, double-click to reset to the default. */}
      <div
        className="review-doc-resize-handle"
        title="גרירה לשינוי רוחב • לחיצה כפולה לאיפוס"
        onMouseDown={(e) => {
          e.preventDefault();
          resizeRef.current = true;
          setResizing(true);
          document.body.style.cursor = 'col-resize';
          document.body.style.userSelect = 'none';
        }}
        onDoubleClick={() => {
          setWidthPct(defaultWidth);
          localStorage.setItem(widthKey, String(defaultWidth));
        }}
      />
      <div className="review-doc-toolbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          {/* Page nav only for MULTI-FILE documents. A single multi-page PDF is
              one file — the browser's own PDF viewer scrolls its pages, so a
              second pager here would just be noise. */}
          {multi && (
            <>
              <button type="button" className="btn btn-secondary btn-sm" disabled={idx === 0}
                onClick={() => setPageIdx(i => Math.max(0, i - 1))} title="עמוד קודם"><Icon name="chevron-inline" size={14} flip /></button>
              <span style={{ fontWeight: 700, color: '#1a1a2e', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                {idx + 1}/{list.length}
              </span>
              <button type="button" className="btn btn-secondary btn-sm" disabled={idx === list.length - 1}
                onClick={() => setPageIdx(i => Math.min(list.length - 1, i + 1))} title="עמוד הבא"><Icon name="chevron-inline" size={14} /></button>
            </>
          )}
          {!multi && <span style={{ fontWeight: 700, color: 'var(--ink-muted)', fontSize: '0.82rem' }}>מסמך מקורי</span>}
          {/* Zoom is IMAGE-only. PDFs use the browser viewer's own zoom, so
              adding buttons here would duplicate it and (for PDFs) do nothing. */}
          {!isPdf && (
            <>
              <button type="button" style={zoomBtn} onClick={() => setZoom(z => Math.min(4, Math.round((z + 0.25) * 100) / 100))} title="הגדל">＋</button>
              <span style={{ fontSize: '0.76rem', color: 'var(--ink-muted)', minWidth: 34, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
              <button type="button" style={zoomBtn} onClick={() => setZoom(z => Math.max(0.5, Math.round((z - 0.25) * 100) / 100))} title="הקטן">－</button>
            </>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {onRotate && cur && (
            <button type="button" className="btn btn-outline btn-sm"
              onClick={() => { setPan({ x: 0, y: 0 }); onRotate(cur); }}
              title="סיבוב העמוד ב-90° (נשמר לכולם)" aria-label="סיבוב העמוד">
              <Icon name="rotate-cw" size={14} />
            </button>
          )}
          <button type="button" className="btn btn-outline btn-sm" onClick={openExternal}
            title="פתיחה בלשונית נפרדת (למסך שני)" aria-label="פתח בלשונית נפרדת">
            {/* Standard "external link" glyph */}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </button>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => setCollapsed(true)}
            title="קפל את חלונית המסמך לצד" aria-label="קפל את חלונית המסמך">⟩</button>
        </div>
      </div>

      <div className="review-doc-body">
        {loading && (
          <div className="loading-container" style={{ padding: 24 }}><div className="spinner" /><span>טוען מסמך...</span></div>
        )}
        {!loading && error && (
          <div className="empty-state" style={{ padding: 24 }}>
            <Icon name="alert" size={34} style={{ color: '#9BAAA2', marginBottom: 10 }} />
            <p>שגיאה בטעינת הקובץ — נסי לרענן את העמוד</p>
          </div>
        )}
        {!loading && !error && url && (
          isPdf ? (
            // toolbar=0 strips Chrome's built-in PDF chrome — both the thumbnail
            // sidebar and the crowded top toolbar — leaving just the page; view=FitH
            // fits it to the pane width so it's readable by default. Deep zoom /
            // print stay one click away via "פתח בחלון נפרד". (Standard PDF open
            // parameters; harmless in viewers that ignore them.)
            <RotatedFrame
              src={`${url}#toolbar=0&view=FitH`}
              title={`עמוד ${idx + 1}`}
              rotation={rotation}
              style={{ width: '100%', height: '100%', minHeight: 480, border: 'none', background: '#fff' }}
            />
          ) : (
            <div
              style={{ width: '100%', height: '100%', overflow: 'hidden', cursor: dragRef.current ? 'grabbing' : 'grab', background: '#f2f4f8' }}
              onMouseDown={(e) => {
                e.preventDefault();
                dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: pan.x, baseY: pan.y };
              }}
              onDoubleClick={() => { setZoom(z => (z === 1 ? 2 : 1)); setPan({ x: 0, y: 0 }); }}
              title="גרירה להזזה • לחיצה כפולה לזום"
            >
              {/* Pan + zoom move the page as a whole; the turn is inside it. */}
              <div
                style={{
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transformOrigin: 'center top',
                  transition: dragRef.current ? 'none' : 'transform 0.1s ease-out',
                }}
              >
                <RotatedImg
                  src={url}
                  alt={`עמוד ${idx + 1}`}
                  draggable={false}
                  rotation={rotation}
                  style={{ maxWidth: '100%', display: 'block', margin: '0 auto', userSelect: 'none' }}
                />
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}
